import isFalsy from '../helpers/is-falsy';
import isTruthy from '../helpers/is-truthy';

const invoiceLines = {
  'Import ID': {
    requirementType: 'required',
    description:
      'Unique identifier for the imported record. Imported row will be checked for duplicate. If a duplicate is found, the row will be skipped from import.',
    dataType: 'string',
    validator: (value) => {
      return value !== undefined && value !== null && value.trim() !== '';
    },
  },
  'Project Name': {
    requirementType: 'required',
    description:
      'The name of the project associated with the invoice line. This field must match the name of a project exactly, including spaces, punctuation and capitalization, otherwise the row may be skipped during import.',
    dataType: 'string',
    validator: (value) => value.trim() !== '',
  },
  'Invoice Number': {
    requirementType: 'required',
    description:
      'Each invoice has a number, it does not need to be numeric but it must be unique otherwise the row will be skipped.',
    dataType: 'string',
    validator: () => {
      return true;
    },
  },
  Amount: {
    requirementType: 'required',
    description:
      'Amount value of the invoice line; if Quantity and Rate are provided, Amount must equal Quantity × Rate (to the cent).',
    dataType: 'number',
    validator: (value, row) => {
      // allow 0; reject empty/invalid
      const amount = toNum(value);
      const amountProvided = value !== undefined && value !== null && String(value).trim() !== '';
      const isValidAmount = amountProvided && amount !== null;

      const qProvided =
        row['Quantity'] !== undefined && row['Quantity'] !== null && String(row['Quantity']).trim() !== '';
      const rProvided = row['Rate'] !== undefined && row['Rate'] !== null && String(row['Rate']).trim() !== '';

      const qty = toNum(row['Quantity']);
      const rate = toNum(row['Rate']);

      const isValidQuantity = qProvided && qty !== null && qty >= 0;
      const isValidRate = rProvided && rate !== null && rate >= 0;

      // If both qty & rate are present, compare in cents to avoid FP errors
      let matchesQuantityAndRate = true;
      if (isValidQuantity && isValidRate) {
        const expected = qty * rate;
        matchesQuantityAndRate = toCents(amount) === toCents(expected);
      }

      return isValidAmount && matchesQuantityAndRate;
    },
  },
  Quantity: {
    requirementType: 'conditional',
    description: 'Quantity of the invoice line, along with Rate will be used to check Amount.',
    dataType: 'number',
    validator: () => true,
  },
  Rate: {
    requirementType: 'conditional',
    description: 'Rate of the invoice line, along with Quantity will be used to check Amount.',
    dataType: 'number',
    validator: () => true,
  },
  'Invoice Item': {
    requirementType: 'conditional',
    description:
      'Invoice Item of the ledger item; required if ledger is "milestone" or "other" item to bill. Specify the Invoice Item that you wish to be associated with the item being created on the ledger. If not found the default will be automatically assigned.',
    dataType: 'string',
    helpUrl:
      'https://help.ruddr.io/faqs-and-troubleshooting/billing/how-do-default-invoice-items-get-applied-in-invoices',
    validator: (value, row) => {
      const r =
        (row['Ledger'] !== 'other' && row['Ledger'] !== 'milestone') ||
        ((row['Ledger'] === 'other' || row['Ledger'] === 'milestone') && value.trim() !== '');
      return r;
    },
  },

  Description: {
    requirementType: 'optional',
    description: 'Description on an invoice line where details could be provided.',
    dataType: 'string',
    validator: () => true,
  },
  'Line Number': {
    requirementType: 'required',
    description: 'The whole integer value of the line number, this should be unique per invoice number.',
    dataType: 'numeric',
    validator: (value) => {
      if (value == null || value === '') return false;

      // Clean and parse the number (handles stray characters, commas, spaces)
      const cleaned = String(value)
        .trim()
        .replace(/[^\d-]/g, '');
      const num = parseInt(cleaned, 10);

      if (!Number.isInteger(num) || num <= 0) return false;
      return true;
    },
  },

  Taxable: {
    requirementType: 'optional',
    description:
      'Provide "Yes" if you consider the line as taxable on the invoice. Otherwise will be considered not taxable.',
    dataType: 'boolean',
    validator: (value) => {
      return !value || isTruthy(value) || isFalsy(value);
    },
  },

  'Transaction Type': {
    requirementType: 'optional',
    description: `Provide the transaction type for the invoice line: 'time', 'milestone', 'expense', 'product_item', 'service_item', or 'other_item'. The legacy values 'product', 'service', and 'other' are also accepted and mapped to their item transaction types. Use 'adhoc' or leave this blank for a line without any association to project ledgers.`,
    dataType: 'string',
    validator: (value) =>
      !value ||
      [
        'time',
        'milestone',
        'expense',
        'product_item',
        'service_item',
        'other_item',
        'product',
        'service',
        'other',
        'adhoc',
      ].includes(value.trim().toLowerCase()),
  },
};

// helpers
const toNum = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const toCents = (n) => Math.round(n * 100); // 2-decimal currency

export default invoiceLines;
